import { createDebugLogger } from './debug-logger';

type TimerAdd<D> = {
  name: string;
  startedAt: number;
  data: D;
};

type TimerGet<D> = TimerAdd<D> & {
  elapsedMs: number;
};

type TimersMap<D> = Map<string, TimerAdd<D>>;

type Options = {
  debug?: boolean;
  throwOnOverwrite?: boolean;
};

export class Timers<Data> {
  _timers: TimersMap<Data>;
  _options: Options;
  _logger: any;

  constructor(options: Options = {}) {
    this._options = options;
    this._timers = new Map<string, TimerAdd<Data>>();
    this._logger = createDebugLogger('[Timers]:', this._options.debug);
  }

  start(name: string, data: Data, overwrite?: boolean) {
    this._logger('Starting timer', { name, data, overwrite });

    if (
      this._options.throwOnOverwrite &&
      !overwrite &&
      this._timers.get(name)
    ) {
      throw new Error('Timer already exists');
    }

    this._timers.set(name, { data, name, startedAt: performance.now() });
  }

  get(name: string): TimerGet<Data> | undefined {
    this._logger('Getting timer', name);

    const timer = this._timers.get(name);

    if (timer) {
      const elapsedMs = Math.round(performance.now() - timer.startedAt);

      return {
        ...timer,
        elapsedMs,
      };
    }
  }

  delete(name: string) {
    this._logger('Deleting timer', name);

    this._timers.delete(name);
  }

  reset(name: string): boolean {
    this._logger('Resetting timer', name);

    const timer = this._timers.get(name);

    if (timer) {
      this._timers.set(name, {
        ...timer,
        startedAt: performance.now(),
      });
      return true;
    }

    return false;
  }
}
